import React, { useState, useEffect } from "react";
import { CircularProgress, Box, FormControlLabel, Switch, Checkbox, Radio } from "@mui/material";
import PropTypes from "prop-types";
import ListSelect from "../../../components/Utils/ListSelect";
import {
	checkInstructorResultsEmail,
	sendInstructorResultsEmail,
	checkAllDepartmentsForResultsEmail,
} from "../../../components/Api/Getters";

function CustomTabPanel(props) {
	const { children, value, index, ...other } = props;

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && <Box sx={{ p: 3 }}>{children}</Box>}
		</div>
	);
}

CustomTabPanel.propTypes = {
	children: PropTypes.node,
	index: PropTypes.number.isRequired,
	value: PropTypes.number.isRequired,
};

export default function EmailSPOTResults(props) {
	const {
		value,
		terms,
		termValue,
		handleChangeTerm,
		termNumber,
		allDepartmentsResults,
		loadingCheckDepartments,
		instructorsForEmail,
		resultsMessage,
		setResultsMessage,
		token,
		setInstructorsForEmail,
		setLoadingCheckDepartments,
		setAllDepartmentsResults,
	} = props;

	const [multiDepartmentEmail, setMultiDepartmentEmail] = useState(false);
	const [loadingResultsEmails, setLoadingResultsEmails] = useState(false);
	const [deptIdArray, setDeptIdArray] = useState([]);
	const [deptId, setDeptId] = useState(null);

	useEffect(() => {
		if (termNumber) {
			const fetchData = async () => {
				await handleCheckDepartmentsForResultsEmail();
			};
			fetchData();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [termNumber]);

	useEffect(() => {
		// if there’s nothing to do, clear out and bail
		if (!deptIdArray || deptIdArray.length === 0) {
			setInstructorsForEmail([]);
			return;
		}

		// define an async loader inside the effect
		const fetchAllInstructors = async () => {
			try {
				// kick off one promise per deptId
				const allPromises = deptIdArray.map((deptId) =>
					handleCheckInstructorResultsEmail(deptId, termNumber, "multi"),
				);
				// wait for them all
				const results = await Promise.all(allPromises);
				// flatten and filter out any empty/null arrays
				const total = results.filter((arr) => Array.isArray(arr) && arr.length > 0).flat();
				setInstructorsForEmail(total);
			} catch (err) {
				console.error("failed to fetch instructors:", err);
				setInstructorsForEmail([]); // or leave unchanged, up to you
			}
		};

		fetchAllInstructors();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [deptIdArray, termNumber]);

	const handleCheckInstructorResultsEmail = async (deptId, term, singleOrMulti) => {
		try {
			setResultsMessage("");
			const response = await checkInstructorResultsEmail(token, deptId, term);
			if (response.data_classes && response.data_classes.length > 0) {
				if (singleOrMulti === "single") {
					setInstructorsForEmail(response.data_classes);
					setResultsMessage("Ready to send.");
				} else if (singleOrMulti === "multi") {
					return response.data_classes;
				}
			} else {
				setResultsMessage(response.message);
			}
		} catch (error) {
			console.error(error);
		}
	};

	const handleSendEmails = async (singleOrMulti) => {
		try {
			setLoadingResultsEmails(true);
			if (singleOrMulti === "single") {
				const response = await sendInstructorResultsEmail(
					token,
					deptId,
					termNumber,
					termValue.split(" ")[0].concat(" ").concat(termValue.split(" ")[1]),
				);
				setResultsMessage(response.message);
				const response2 = await checkInstructorResultsEmail(token, deptId, termNumber);
				if (response2.data_classes && response2.data_classes.length > 0) {
					setInstructorsForEmail(response2.data_classes);
				} else {
					setInstructorsForEmail([]);
				}
				setLoadingResultsEmails(false);

				handleCheckDepartmentsForResultsEmail();
			} else if (singleOrMulti === "multi") {
				if (deptIdArray.length === 0) {
					setResultsMessage("Please select at least one department.");
					setLoadingResultsEmails(false);
					return;
				}
				if (deptIdArray.length >= 1) {
					const selectedDeptIds = [...deptIdArray];
					const sendAllEmails = async () => {
						try {
							const results = await Promise.allSettled(
								selectedDeptIds.map((departmentId) =>
									sendInstructorResultsEmail(
										token,
										departmentId,
										termNumber,
										termValue.split(" ").slice(0, 2).join(" "),
									),
								),
							);

							const successfulDeptIds = results
								.map((result, index) =>
									result.status === "fulfilled" ? selectedDeptIds[index] : null,
								)
								.filter((departmentId) => departmentId !== null);

							const totalEmailsSent = results.reduce((sum, result) => {
								if (result.status !== "fulfilled") {
									return sum;
								}

								return sum + (result.value.count_emails || 0);
							}, 0);

							if (successfulDeptIds.length > 0) {
								setDeptIdArray((currentDeptIds) =>
									currentDeptIds.filter(
										(currentDeptId) => !successfulDeptIds.includes(currentDeptId),
									),
								);
							}

							const failedDepartmentsCount = selectedDeptIds.length - successfulDeptIds.length;

							if (successfulDeptIds.length === 0) {
								setResultsMessage("Something went wrong sending emails.");
							} else if (failedDepartmentsCount > 0) {
								setResultsMessage(
									`Emails sent to ${totalEmailsSent} instructors across ${successfulDeptIds.length} departments. ${failedDepartmentsCount} departments still need attention.`,
								);
							} else {
								setResultsMessage(
									`Emails sent to ${totalEmailsSent} instructors across ${successfulDeptIds.length} departments.`,
								);
							}

							await handleCheckDepartmentsForResultsEmail();
						} catch (err) {
							console.error("failed to send emails:", err);
							setResultsMessage("Something went wrong sending emails.");
						}
					};
					await sendAllEmails();
					setLoadingResultsEmails(false);
				}
			}
		} catch (error) {
			console.error(error);
			setResultsMessage("Error sending emails");
			setLoadingResultsEmails(false);
		}
	};

	const handleCheckDepartmentsForResultsEmail = async () => {
		setLoadingCheckDepartments(true);
		try {
			const response = await checkAllDepartmentsForResultsEmail(token, termNumber);
			if (response.data_classes && response.data_classes.length > 0) {
				setAllDepartmentsResults(response.data_classes);
				setLoadingCheckDepartments(false);
			} else {
				setAllDepartmentsResults([]);
				setLoadingCheckDepartments(false);
			}
		} catch (error) {
			console.error(error);
			setLoadingCheckDepartments(false);
		}
	};
	return (
		<CustomTabPanel value={value} index={0}>
			<div className="flex gap-4">
				{/* Left Sidebar: Term and Department List */}
				<div className="w-1/3">
					<div className="">
						<ListSelect
							className="termSelect"
							list={terms}
							label="Term"
							width="100%"
							value={termValue || ""}
							onChange={handleChangeTerm}
						/>
					</div>
					<div className="bg-gray-100 shadow rounded p-2">
						<h3 className="text-lg font-semibold mb-2">Departments</h3>
						<FormControlLabel
							control={
								<Switch
									checked={multiDepartmentEmail}
									onChange={(e) => {
										setMultiDepartmentEmail(e.target.checked);

										setDeptId(null);
										setInstructorsForEmail([]);
									}}
								/>
							}
							label="Send to Multiple Departments"
						/>
						{loadingCheckDepartments ? (
							<div className="max-h-80 overflow-y-auto gap-2 flex flex-col">
								<CircularProgress size={20} />
							</div>
						) : (
							<div className="max-h-80 overflow-y-auto gap-2 flex flex-col">
								{allDepartmentsResults && allDepartmentsResults.length > 0 ? (
									allDepartmentsResults.map((department) =>
										multiDepartmentEmail ? (
											<FormControlLabel
												key={department.dept_id}
												label={
													<span className="text-left">
														{department.dept_id__display_name} ({department.unique_empid_count})
													</span>
												}
												control={
													<Checkbox
														checked={deptIdArray.includes(department.dept_id)}
														onChange={(e) => {
															if (e.target.checked) {
																setDeptIdArray([...deptIdArray, department.dept_id]);
															} else {
																setDeptIdArray(
																	deptIdArray.filter((id) => id !== department.dept_id),
																);
															}
														}}
													/>
												}
											/>
										) : (
											<FormControlLabel
												key={department.dept_id}
												label={
													<span className="text-left">
														{department.dept_id__display_name} ({department.unique_empid_count})
													</span>
												}
												control={
													<Radio
														checked={deptId === department.dept_id}
														onChange={() => {
															setDeptId(department.dept_id);
															handleCheckInstructorResultsEmail(
																department.dept_id,
																termNumber,
																"single",
															);
														}}
														value={department.dept_id}
														name="department-radio"
													/>
												}
											/>
										),
									)
								) : (
									<p>No departments available</p>
								)}
							</div>
						)}
					</div>
				</div>

				{/* Main Panel: Instructors and Email Actions */}
				<div className="w-2/3">
					{instructorsForEmail && instructorsForEmail.length > 0 ? (
						<div className="bg-gray-100 shadow rounded p-2">
							<h3 className="text-lg font-semibold mb-2">Instructors for Notification</h3>
							<div className="max-h-80 overflow-y-auto">
								{instructorsForEmail.map((instructor, index) => (
									<div
										key={instructor.empid}
										className="flex justify-between items-center p-2 border-b bg-white rounded-xl"
									>
										<span className="w-1/3">#{index + 1}</span>
										<span
											className="text-left w-1/3"
											title={instructor.classes.map((item) => item.classid).join(",")}
										>
											{instructor.classes.length > 1
												? `${instructor.classes.length} Classes`
												: `1 Class`}
										</span>
										<span className="w-1/3">{instructor.email}</span>
									</div>
								))}
							</div>
							{multiDepartmentEmail ? (
								<button
									onClick={() => handleSendEmails("multi")}
									disabled={loadingResultsEmails || instructorsForEmail.length === 0}
									className="mt-4 w-full py-2 bg-green-700 border-2 border-green-800 rounded hover:bg-green-900 text-white disabled:bg-gray-400 disabled:border-gray-500"
								>
									{loadingResultsEmails ? (
										<CircularProgress size={20} />
									) : (
										`Send Emails to ${instructorsForEmail.length} Instructors`
									)}
								</button>
							) : (
								<button
									onClick={() => handleSendEmails("single")}
									disabled={loadingResultsEmails || instructorsForEmail.length === 0}
									className="mt-4 w-full py-2 bg-green-700 border-2 border-green-800 rounded hover:bg-green-900 text-white disabled:bg-gray-400 disabled:border-gray-500"
								>
									{loadingResultsEmails ? (
										<CircularProgress size={20} />
									) : (
										`Send Emails to ${instructorsForEmail.length} Instructors`
									)}
								</button>
							)}

							{resultsMessage && (
								<p className="mt-2 p-2 bg-white border border-gray-400 rounded">
									<strong>Message:</strong> {resultsMessage}
								</p>
							)}
						</div>
					) : (
						<div className="bg-white border-gray-200 border-2 shadow rounded p-2">
							<p>Please select a department to see instructors.</p>
						</div>
					)}
				</div>
			</div>
		</CustomTabPanel>
	);
}
